import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES,
  ACTIVE_ACCOUNT_COOKIE_NAME,
  AUTH_TOKENS_COOKIE_ATTRIBUTES,
  AUTH_TOKENS_COOKIE_NAME,
} from '@common/constants';
import { EAppRoutes, Permission, UserIdentityProvider } from '@common/enums';
import appConfig from '@configs/app-config';
import { AuthContextState, UserScope } from './interfaces';
import { fetchUserScope, runDevLogin, userScopeHasPermissions } from '../../utils/auth/authUtils';

export const useAuth = (): AuthContextState => {
  const cookie = Cookies.get(AUTH_TOKENS_COOKIE_NAME);
  const location = useLocation();
  const navigate = useNavigate();

  const parsedCookie = cookie ? JSON.parse(cookie) : {};

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isA1Session, setIsA1Session] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userScope, setUserScope] = useState<UserScope | undefined>(parsedCookie.user_scope);

  /**
   * The below effect runs authentication logic on every route change.
   */
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const state = queryParams.get('state');
    const code = queryParams.get('code');

    // Run all auth logic
    (async (): Promise<void> => {
      try {
        // Run login for local dev, only on local do we need to handle the token exchange directly
        if (window.location.hostname === 'localhost' && state && code) {
          await runDevLogin(state, code);
        }

        // Fetch UserScope
        const fetchedUserScope = await fetchUserScope();
        if (fetchedUserScope) {
          setUserScope(fetchedUserScope);
          setIsAuthenticated(true);
          setIsA1Session(false);
        } else {
          setUserScope(undefined);
          setIsAuthenticated(false);
          setIsA1Session(false);
        }

        // Loading has finished
        setIsLoading(false);
      } catch (error) {
        Cookies.remove(AUTH_TOKENS_COOKIE_NAME, AUTH_TOKENS_COOKIE_ATTRIBUTES);
        Cookies.remove(ACTIVE_ACCOUNT_COOKIE_NAME, ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES);
        console.error(error);
        logout();
      }
    })();
  }, [location.pathname]);

  const logout = (): void => {
    const userIdentityProvider = userScope?.identity_provider_id;

    // Clear local session
    Cookies.remove(AUTH_TOKENS_COOKIE_NAME, AUTH_TOKENS_COOKIE_ATTRIBUTES);
    Cookies.remove(ACTIVE_ACCOUNT_COOKIE_NAME, ACTIVE_ACCOUNT_COOKIE_ATTRIBUTES);
    setUserScope(undefined);
    setIsAuthenticated(false);
    setIsA1Session(false);

    // Redirect to OIDC provider's logout endpoint
    if (userIdentityProvider === UserIdentityProvider.Okta) {
      navigate(EAppRoutes.Login);
    } else if (userIdentityProvider === UserIdentityProvider.Auth0) {
      window.location.href = `${appConfig.auth0Url}/v2/logout?client_id=${
        appConfig.auth0ClientId
      }&returnTo=${encodeURIComponent(appConfig.logoutRedirectUrl)}`;
    }
  };

  /**
   * Check if user is logged in and has permissions
   */
  const checkAuthAndPermissions = (requiredPermissions?: Permission): boolean => {
    if (!isAuthenticated) {
      return false;
    }
    return !(requiredPermissions && !userScopeHasPermissions(userScope, requiredPermissions));
  };

  return {
    isAuthenticated,
    isA1Session,
    isLoading,
    userScope,
    logout,
    checkAuthAndPermissions,
  };
};
